import { useSetState } from 'react-use';
import AdvancedStatusInput from '../../Status/AdvancedStatusInput';

import { NewPaperWithPadding } from '@components/Misc';
import { Grid, Typography, Checkbox } from '@ts/storybook';
import { RadioButtonGroup } from '@components/Inputs';

import { get } from 'lodash';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import IconTooltip from '@components/Outputs/IconTooltip';

import { IForm } from '@ts/ui-models';
import { IAppProps } from '@interfaces/App.interface';

interface IEventTypeItemRulesTabProps extends IForm, IAppProps {}

const EventTypeItemRulesTab = (props: IEventTypeItemRulesTabProps) => {
  const { values, onCallbackChange } = props;
  const isModeView = props.mode == 'view';
  const [state, setState] = useSetState({
    changeLocationMode: values.replaceLocations === true ? 'replace' : 'add',
  });

  //#region Handlers
  const onChangeGeneric = (entity, field) => {
    if (entity != null) {
      onCallbackChange(field + 'Id')(entity.getID());
      onCallbackChange(field)(entity);
    } else {
      onCallbackChange(field + 'Id')(null);
      onCallbackChange(field)(null);
    }
  };
  //#endregion

  //#region Renders
  const _renderItemStatus = () => {
    let out: any[] = [];
    let shouldUpdateItemStatus = values.updatesItemsStatus;

    out.push(
      <Checkbox
        key="chkbx"
        disabled={isModeView}
        label="Update the status of items affected by an event of this type"
        checked={shouldUpdateItemStatus}
        style={{ marginBottom: 16, marginTop: 15, fontSize: 14 }}
        onChange={(ev, isChecked) => props.onChange('updatesItemsStatus', isChecked)}
      />
    );

    if (shouldUpdateItemStatus) {
      const indicators = get(values, 'itemStatusUpdate.indicators') || [];
      out.push(
        <AdvancedStatusInput
          {...props}
          key="sop"
          title={'Indicators to update in items'}
          statesList={indicators}
          onChangeList={newList => {
            const { itemStatusUpdate } = values;
            if (itemStatusUpdate) onCallbackChange('itemStatusUpdate.indicators')(newList);
            else {
              onCallbackChange('itemStatusUpdate')({
                type: 'operation',
                indicators: newList,
              });
            }
          }}
          isAdvancedSearch={false}
        />
      );
    }

    return <div>{out}</div>;
  };

  const _renderItemTypeStatus = () => {
    let out: any[] = [];
    let shouldUpdateItemTypeStatus = values.updatesItemTypesStatus;
    out.push(
      <Checkbox
        key="chkbx"
        disabled={isModeView}
        label="Update the status of the item type of the items affected by an event of this type"
        checked={shouldUpdateItemTypeStatus}
        style={{ marginBottom: 16, marginTop: 15, fontSize: 14 }}
        onChange={(ev, isChecked) => props.onChange('updatesItemTypesStatus', isChecked)}
      />
    );
    if (shouldUpdateItemTypeStatus) {
      const indicators = get(values, 'itemTypeStatusUpdate.indicators') || [];
      out.push(
        <AdvancedStatusInput
          {...props}
          key="sop"
          title={'Indicators to update in item types'}
          statesList={indicators}
          onChangeList={newList => {
            console.log('newList', newList);
            const itemTypeStatusUpdate = values.itemTypeStatusUpdate;
            if (itemTypeStatusUpdate) {
              onCallbackChange('itemTypeStatusUpdate.indicators')(newList);
            } else {
              onCallbackChange('itemTypeStatusUpdate')({
                type: 'operation',
                indicators: newList,
              });
            }
          }}
          isAdvancedSearch={false}
        />
      );
    }

    return <div>{out}</div>;
  };

  const _renderChangeLocation = () => {
    let out: any[] = [];
    const { values, onChange } = props;
    let shouldEnableSwitchLocations = values.locationSwitch;
    let firstLocationLabel = shouldEnableSwitchLocations ? 'First location to switch' : 'New location for the item';
    const locationByEventRemove = values.locationByEventRemove;
    //we cannot use boolean value in the radios
    const locationByEventMode = locationByEventRemove ? 'remove' : 'add';

    /* let labelReplace = 'Replace old locations with new location';
    let labelAdd = 'Add the location without replacing existing ones';
    if (locationByEventRemove) {
      labelReplace = 'Remove the item from all locations';
      labelAdd = 'Remove the item from the provided location';
    } */

    return (
      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 12 }}>
          <Grid container>
            <Typography variant="body1" text={[<b>Location update method</b>]} />
            <IconTooltip text={'Choose how to update the item’s location'} isInfo />
          </Grid>
          <Grid pl={2}>
            <Checkbox
              disabled={isModeView}
              label="Update based on event-provided location"
              checked={values.locationByEvent}
              //style={{ marginBottom: 16, marginTop: 15, fontSize: 14 }}
              onChange={(ev, isChecked) => {
                onChange('locationByEvent', isChecked);
                if (isChecked) {
                  onChange('locationBySelection', !isChecked);
                  onChange('locationByInfrastructure', !isChecked);
                }
              }}
            />
            <Checkbox
              key="locByInfrastructure"
              disabled={isModeView}
              label="Update based on infrastructure location"
              checked={values.locationByInfrastructure}
              //style={{ marginBottom: 16, marginTop: 15, fontSize: 14 }}
              onChange={(ev, isChecked) => {
                onChange('locationByInfrastructure', isChecked);
                if (isChecked) {
                  onChange('locationBySelection', !isChecked);
                  onChange('locationByEvent', !isChecked);
                }
              }}
            />
            <Checkbox
              key="locBySelect"
              disabled={isModeView}
              label="Update to a fixed location"
              checked={values.locationBySelection}
              onChange={(ev, isChecked) => {
                onChange('locationBySelection', isChecked);
                if (isChecked) {
                  onChange('locationByEvent', !isChecked);
                  onChange('locationByInfrastructure', !isChecked);
                }
              }}
            />
            {values.locationBySelection && (
              <Grid pl={4}>
                <WrapperEntitySelector
                  key="newLocSelector"
                  label={firstLocationLabel}
                  value={values.newLocation}
                  onChangeEntity={location => onChangeGeneric(location, 'newLocation')}
                  searchComponent={generateSearchEntitySelector('location')}
                  disabled={isModeView}
                  required={shouldEnableSwitchLocations}
                />
                <Checkbox
                  key="switchLoc"
                  disabled={isModeView}
                  label="Enable switch between two locations"
                  checked={shouldEnableSwitchLocations}
                  onChange={(ev, isChecked) => onChange('locationSwitch', isChecked)}
                />
                {shouldEnableSwitchLocations && (
                  <WrapperEntitySelector
                    key="switchLocSelector"
                    label={'Second location to switch'}
                    value={values.switchLocation}
                    onChangeEntity={location => onChangeGeneric(location, 'switchLocation')}
                    searchComponent={generateSearchEntitySelector('location')}
                    disabled={isModeView}
                    required={shouldEnableSwitchLocations}
                  />
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Grid container>
            <Typography variant="body1" text={[<b>Update location action</b>]} />
            <IconTooltip text={'Specify the action to perform on the item’s location'} isInfo />
          </Grid>
          <Grid pl={2}>
            <RadioButtonGroup
              key="locationByEventRemove"
              value={locationByEventMode}
              onChange={value => {
                const boolValue = value == 'remove' ? true : false;
                onChange('locationByEventRemove', boolValue);
              }}
              disabled={isModeView}
              options={[
                {
                  value: 'add',
                  label: 'Add the item to the specified location',
                },
                {
                  value: 'remove',
                  label: 'Remove the item from the specified location',
                },
              ]}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Grid container>
            <Typography variant="body1" text={[<b>Multi-location handling</b>]} />
            <IconTooltip text={'Choose how to handle existing locations'} isInfo />
          </Grid>
          <Grid pl={2}>
            <RadioButtonGroup
              key="radio"
              value={state.changeLocationMode}
              onChange={value => {
                const realValue = value == 'replace';
                onChange('replaceLocations', realValue);
                setState({ changeLocationMode: value });
              }}
              disabled={isModeView}
              options={[
                {
                  value: 'replace',
                  label:
                    locationByEventMode === 'add'
                      ? 'Replace old locations with new location'
                      : 'Remove the item from all locations',
                },
                {
                  value: 'add',
                  label:
                    locationByEventMode === 'add'
                      ? 'Add the location without replacing existing ones'
                      : 'Remove the item from the provided location',
                },
              ]}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderHierarchy = () => {
    return (
      <Checkbox
        label="Break hierarchy relations with parents"
        checked={values.breakParentRelations}
        onChange={(ev, isChecked) => props.onChange('breakParentRelations', isChecked)}
        disabled={isModeView}
      />
    );
  };
  //#endregion

  return (
    <Grid container spacing={3} pb={4}>
      <Grid size={{ xs: 12 }}>
        <NewPaperWithPadding>
          <Typography variant="h6" text="Hierarchy" />
          <Grid size={{ xs: 12 }} py={2}>
            {renderHierarchy()}
          </Grid>
        </NewPaperWithPadding>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <NewPaperWithPadding>
          <Typography variant="h6" text="Change status" />
          <Grid size={{ xs: 12 }} px={2}>
            {_renderItemStatus()}
            {_renderItemTypeStatus()}
          </Grid>
        </NewPaperWithPadding>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <NewPaperWithPadding>
          <Typography variant="h6" text="Change location" />
          <Grid size={{ xs: 12 }} px={2}>
            {_renderChangeLocation()}
          </Grid>
        </NewPaperWithPadding>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <NewPaperWithPadding>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" text="Change labels" />
              <Grid size={{ xs: 12 }} px={2} mt={2}>
                <Typography variant="body1" text="Labels to add when an event of this type happens" />
                <WrapperEntitySelector
                  searchComponent={generateSearchEntitySelector('tag')}
                  value={values.labelsAdd}
                  onChange={onCallbackChange('labelsAdd')}
                  disabled={isModeView}
                  multiple
                />
                <Typography variant="body1" text="Labels to remove when an event of this type happens" className="mt-px" />
                <WrapperEntitySelector
                  searchComponent={generateSearchEntitySelector('tag')}
                  value={values.labelsRemove}
                  onChange={onCallbackChange('labelsRemove')}
                  disabled={isModeView}
                  multiple
                />
              </Grid>
            </Grid>
          </Grid>
        </NewPaperWithPadding>
      </Grid>
    </Grid>
  );
};

export default EventTypeItemRulesTab;
